import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, forwardRef, Input, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, UntypedFormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';

import { ValidCpfDirective } from '@app/shared/validators/valid-cpf.directive';
import { ValidCnpjDirective } from '@app/shared/validators/valid-cnpj.directive';

@Component({
  selector: 'app-cpf-cnpj-input',
  templateUrl: './cpf-cnpj-input.component.html',
  styleUrls: ['./cpf-cnpj-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CpfCnpjInputComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => CpfCnpjInputComponent),
      multi: true
    }
  ],
  standalone: false
})
export class CpfCnpjInputComponent implements ControlValueAccessor, Validator, AfterViewInit {
  @Input() inputClass = '';
  @Input() disabled = false;
  @Input() required = false;
  @Input() cpfOnly = false;
  @Input() name = 'cpf-cnpj';
  @Input() autocomplete = 'none';
  @Input() label = '';
  @Input() tabindex?: number | string;
  @Input() dropSpecialCharacters = false;
  @Input() hasError = false;
  @Input() enableValidation = true;
  @Output() validationError = new EventEmitter<boolean>();
  @ViewChild('inputElement') inputElement: ElementRef<HTMLInputElement>;

  value: string = '';
  isCnpj = false;
  inputId = 'cpf-cnpj-input-' + Math.floor(Math.random() * 1000000);
  onChange: (_: string) => void;
  onTouch: () => void;
  onValidationChange: () => void;

  constructor(private cdr: ChangeDetectorRef) {
    this.onChange = () => {};
    this.onTouch = () => {};
    this.onValidationChange = () => {};
  }

  ngAfterViewInit() {
    if (this.value) {
      const sanitized = this.value.replace(/\D/g, '');
      this.isCnpj = !this.cpfOnly && sanitized.length > 11;
    }
  }

  writeValue(value: string) {
    this.value = value || '';
    if (this.value) {
      const sanitized = this.value.replace(/\D/g, '');
      this.isCnpj = !this.cpfOnly && sanitized.length > 11;
      this.onTouch();
    }
  }

  registerOnChange(fn: (_: any) => {}) {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => {}) {
    this.onTouch = fn;
  }

  registerOnValidatorChange(fn: () => void) {
    this.onValidationChange = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  onValueChange(): void {
    const sanitized = this.value ? this.value.replace(/\D/g, '') : '';
    const wasCnpj = this.isCnpj;
    const shouldBeCnpj = !this.cpfOnly && sanitized.length > 11;

    if (wasCnpj !== shouldBeCnpj) {
      setTimeout(() => {
        this.isCnpj = shouldBeCnpj;
        this.cdr.detectChanges();
        if (this.inputElement) {
          this.inputElement.nativeElement.focus();
        }
      });
    } else {
      this.isCnpj = shouldBeCnpj;
    }

    this.emitValue();
  }

  private emitValue(): void {
    this.onChange(this.value);
    this.validateAndEmitError();
  }

  private validateAndEmitError(): void {
    if (!this.enableValidation) {
      this.validationError.emit(false);
      return;
    }

    if (!this.value) {
      this.validationError.emit(false);
      return;
    }

    const hasError = this.isCnpj ? !ValidCnpjDirective.isCnpjValid(this.value) : !ValidCpfDirective.isCpfValid(this.value);

    this.validationError.emit(hasError);
  }

  validate(control: UntypedFormControl): ValidationErrors | null {
    if (!this.enableValidation) {
      return null;
    }

    if (!control.value) {
      return this.required ? { required: true } : null;
    }

    if (this.isCnpj) {
      return ValidCnpjDirective.validator(control, this.enableValidation);
    } else {
      return ValidCpfDirective.validator(control, this.enableValidation);
    }
  }
}
